import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { suppressDropdownOutsideClick } from 'components/DropDown/outsideClickGuard';
import { PersonalDataValidator } from 'utils';

import Input from '../styles/Input';

const validator = new PersonalDataValidator();

/**
 * Google Places session token (UUID). Autocomplete requests sharing this token
 * + the following Place Details call are billed as one session.
 * @see https://developers.google.com/maps/documentation/places/web-service/place-session-tokens
 */
function createSessionToken() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `sess_${Date.now()}_${Math.random()
    .toString(36)
    .slice(2)}`;
}

/**
 * Cascading Places autocomplete.
 * type=postal → city (needs postal) → street → address.
 */
const AddressAutocomplete = ({
  value,
  readOnly,
  disabled,
  country,
  type,
  postalCode,
  city,
  street,
  name,
  onChange,
  onPlaceSelected,
  placeholder,
  required,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const sessionTokenRef = useRef(createSessionToken());
  const debounceRef = useRef(null);
  const selectingRef = useRef(false);
  const listCleanupRef = useRef(null);
  const isDisabled = readOnly || disabled;

  const setListRef = useCallback(node => {
    if (listCleanupRef.current) {
      listCleanupRef.current();
      listCleanupRef.current = null;
    }
    if (!node) {
      return;
    }
    const suppress = () => suppressDropdownOutsideClick(1000);
    node.addEventListener('mousedown', suppress, true);
    node.addEventListener('click', suppress, true);
    listCleanupRef.current = () => {
      node.removeEventListener('mousedown', suppress, true);
      node.removeEventListener('click', suppress, true);
    };
  }, []);

  const fetchSuggestions = useCallback(
    async input => {
      if (isDisabled) {
        setSuggestions([]);
        return;
      }
      const trimmed = (input || '').trim();
      const postalReady = postalCode && postalCode.trim().length >= 2;
      // Empty city field + known postal → suggest localities for that code
      const cityFromPostal = type === 'city' && trimmed.length === 0 && postalReady;
      if (!cityFromPostal && trimmed.length < 2) {
        setSuggestions([]);
        return;
      }
      if (type === 'city' && !postalReady) {
        setSuggestions([]);
        return;
      }
      if (type === 'street' && !(city && city.trim().length >= 2)) {
        setSuggestions([]);
        return;
      }
      if (type === 'address' && (!(city && city.trim()) || !(street && street.trim()))) {
        setSuggestions([]);
        return;
      }
      try {
        const data = await validator.lookupAddress({
          input: trimmed,
          sessionToken: sessionTokenRef.current,
          country,
          type,
          postalCode,
          city,
          street,
        });
        setSuggestions(data.suggestions || []);
        setOpen(true);
      } catch (e) {
        setSuggestions([]);
      }
    },
    [country, isDisabled, type, postalCode, city, street],
  );

  useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    },
    [],
  );

  useEffect(
    () => () => {
      if (listCleanupRef.current) {
        listCleanupRef.current();
        listCleanupRef.current = null;
      }
    },
    [],
  );

  // Clear suggestions when parent context changes — skip while selecting
  useEffect(() => {
    if (selectingRef.current) {
      return;
    }
    setSuggestions([]);
    setOpen(false);
  }, [postalCode, city, street, type]);

  const handleChange = event => {
    onChange(event);
    const { value: next } = event.target;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => fetchSuggestions(next), 350);
  };

  const handleFocus = () => {
    if (isDisabled) {
      return;
    }
    // Clicking empty city after postal code → show localities for that code
    if (
      type === 'city' &&
      !(value && value.trim()) &&
      postalCode &&
      postalCode.trim().length >= 2
    ) {
      fetchSuggestions('');
    }
  };

  const handleSelect = suggestion => {
    selectingRef.current = true;
    suppressDropdownOutsideClick(1000);
    setOpen(false);
    setSuggestions([]);
    // Fill immediately from suggestion label — no Place Details round-trip
    sessionTokenRef.current = createSessionToken();
    if (onPlaceSelected) {
      onPlaceSelected({}, { type, suggestion });
    }
    setTimeout(() => {
      selectingRef.current = false;
    }, 0);
  };

  return (
    <div style={{ position: 'relative' }} data-address-autocomplete="true">
      <Input
        readOnly={readOnly}
        disabled={disabled}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        name={name}
        placeholder={placeholder}
        type="text"
        required={required}
        autoComplete="new-password"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        data-lpignore="true"
        data-1p-ignore="true"
        data-form-type="other"
        aria-autocomplete="list"
      />
      {open && suggestions.length > 0 && (
        <ul
          ref={setListRef}
          role="listbox"
          style={{
            position: 'absolute',
            zIndex: 300,
            left: 0,
            right: 0,
            margin: 0,
            padding: 0,
            listStyle: 'none',
            background: '#fff',
            border: '1px solid #ddd',
            maxHeight: 220,
            overflowY: 'auto',
          }}
        >
          {suggestions.map(item => (
            <li key={item.placeId} role="option">
              <button
                type="button"
                tabIndex={-1}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  border: 0,
                  background: 'transparent',
                  cursor: 'pointer',
                }}
                onMouseDown={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  suppressDropdownOutsideClick(1000);
                  handleSelect(item);
                }}
              >
                {item.description}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

AddressAutocomplete.propTypes = {
  value: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  country: PropTypes.string,
  type: PropTypes.oneOf(['postal', 'city', 'street', 'address']),
  postalCode: PropTypes.string,
  city: PropTypes.string,
  street: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onPlaceSelected: PropTypes.func,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
};

AddressAutocomplete.defaultProps = {
  readOnly: false,
  disabled: false,
  country: 'PL',
  type: 'street',
  postalCode: '',
  city: '',
  street: '',
  name: 'street',
  onPlaceSelected: null,
  placeholder: 'Ulica*',
  required: true,
};

export default AddressAutocomplete;
